import Input from '../common/Input';

export interface ConstructionProduct {
  id: string;
  productId?: string;
  productName: string;
  productModel?: string;
  quantity: number;
  unitPrice: number;
}

interface ProductCardProps {
  product: ConstructionProduct;
  onUpdate: (id: string, field: keyof ConstructionProduct, value: string | number) => void;
  onDelete: (id: string) => void;
  readOnly?: boolean;
}

export default function ProductCard({ product, onUpdate, onDelete, readOnly = false }: ProductCardProps) {
  const subtotal = (product.quantity || 0) * (product.unitPrice || 0);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(amount);
  };

  if (readOnly) {
    return (
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
        <div className="flex justify-between items-start mb-2">
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white">{product.productName || '(未入力)'}</h4>
            {product.productModel && (
              <p className="text-sm text-gray-500 dark:text-gray-400">型番: {product.productModel}</p>
            )}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-gray-500 dark:text-gray-400">数量</span>
            <p className="font-medium text-gray-900 dark:text-white">{product.quantity || 0}</p>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">単価</span>
            <p className="font-medium text-gray-900 dark:text-white">{formatCurrency(product.unitPrice || 0)}</p>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">小計</span>
            <p className="font-medium text-blue-600">{formatCurrency(subtotal)}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800 shadow-sm">
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 mr-4">
          <Input
            label="製品名"
            value={product.productName}
            onChange={(e) => onUpdate(product.id, 'productName', e.target.value)}
            placeholder="製品名を入力"
          />
        </div>
        <button
          type="button"
          onClick={() => onDelete(product.id)}
          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-colors mt-6"
          title="削除"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <Input
          label="型番"
          value={product.productModel || ''}
          onChange={(e) => onUpdate(product.id, 'productModel', e.target.value)}
          placeholder="型番"
        />
        <Input
          label="数量"
          type="number"
          value={product.quantity?.toString() || ''}
          onChange={(e) => onUpdate(product.id, 'quantity', parseInt(e.target.value) || 0)}
          min={0}
        />
        <Input
          label="単価"
          type="number"
          value={product.unitPrice?.toString() || ''}
          onChange={(e) => onUpdate(product.id, 'unitPrice', parseFloat(e.target.value) || 0)}
          min={0}
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">小計</label>
          <div className="h-10 flex items-center px-3 bg-gray-100 dark:bg-gray-700 rounded-lg text-blue-600 dark:text-blue-400 font-medium">
            {formatCurrency(subtotal)}
          </div>
        </div>
      </div>
    </div>
  );
}
